import { Link } from "react-router-dom";
import { Sun, Users, ShoppingCart, Home, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const useCases = [
  {
    id: "geradores",
    icon: Sun,
    title: "Geradores e Usinas Solares",
    subtitle: "Para quem gera e distribui créditos de energia",
    description: "Gerencie múltiplas usinas e distribua créditos para dezenas ou centenas de UCs. Automatize o processo que hoje consome dias de trabalho manual.",
    color: "from-amber-500 to-orange-500",
    painPoints: [
      "Consolidação manual de faturas de várias distribuidoras",
      "Cálculo de rateio em planilhas sujeitas a erro",
      "Cobrança manual e inadimplência alta",
      "Falta de visibilidade para o consumidor",
    ],
    solutions: [
      "Importação automática de faturas de todas distribuidoras",
      "Rateio calculado automaticamente com regras flexíveis",
      "Cobrança via boleto/PIX com notificações automáticas",
      "Portal onde cada consumidor acompanha seus créditos",
    ],
  },
  {
    id: "consorcios",
    icon: Users,
    title: "Consórcios e Cooperativas",
    subtitle: "Para grupos que compartilham geração de energia",
    description: "Administre consórcios de energia com dezenas de participantes. Transparência total na distribuição de créditos e custos.",
    color: "from-blue-500 to-cyan-500",
    painPoints: [
      "Dificuldade em demonstrar como os créditos são divididos",
      "Reuniões para esclarecer dúvidas sobre faturas",
      "Trabalho manual para gerar relatórios de prestação de contas",
      "Gestão de múltiplos perfis de consumo",
    ],
    solutions: [
      "Dashboard transparente com a divisão de cada mês",
      "Portal self-service elimina dúvidas dos participantes",
      "Relatórios automáticos para assembleias e prestação de contas",
      "Regras de rateio que respeitam diferentes perfis",
    ],
  },
  {
    id: "marketplaces",
    icon: ShoppingCart,
    title: "Marketplaces de Energia",
    subtitle: "Para plataformas que conectam geradores e consumidores",
    description: "Escale sua operação sem escalar proporcionalmente sua equipe. Gerencie milhares de UCs com a mesma estrutura.",
    color: "from-violet-500 to-purple-500",
    painPoints: [
      "Operação não escala com o crescimento de clientes",
      "Custo operacional alto por UC gerenciada",
      "Dificuldade em manter qualidade com volume",
      "Integração com sistemas próprios",
    ],
    solutions: [
      "Automação end-to-end permite crescer sem aumentar equipe",
      "Custo por UC diminui drasticamente com automação",
      "Processos padronizados garantem qualidade em escala",
      "API completa para integrar com seu sistema",
    ],
  },
  {
    id: "consumidor",
    icon: Home,
    title: "Consumidor Final",
    subtitle: "Para quem recebe créditos de energia",
    description: "Acesso simples e transparente aos créditos recebidos, faturas e histórico de economia.",
    color: "from-emerald-500 to-teal-500",
    painPoints: [
      "Não entende como os créditos são calculados",
      "Dificuldade para acessar faturas e comprovantes",
      "Não sabe quanto está economizando",
      "Precisa ligar para tirar dúvidas básicas",
    ],
    solutions: [
      "Explicação clara de como os créditos são distribuídos",
      "Download de faturas e boletos direto no portal",
      "Gráficos de economia acumulada mês a mês",
      "FAQ e suporte sem precisar ligar",
    ],
  },
];

export default function CasosDeUso() {
  return (
    <Layout>
      <PageHero
        title="Casos de uso para cada perfil"
        subtitle="Veja como a Cogni resolve desafios específicos para geradores, consórcios, marketplaces e consumidores."
      />

      <section className="section-padding">
        <div className="container-wide">
          <div className="space-y-10 sm:space-y-16">
            {useCases.map((useCase, index) => (
              <Card 
                key={useCase.id} 
                className="border-border overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Info Side */}
                  <div className={`p-5 sm:p-8 md:p-10 ${index % 2 === 0 ? 'section-alt' : 'section-warm'}`}>
                    <CardHeader className="p-0 mb-4 sm:mb-6">
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${useCase.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}>
                        <useCase.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl sm:text-2xl md:text-3xl">{useCase.title}</CardTitle>
                      <CardDescription className="text-sm sm:text-base">{useCase.subtitle}</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="text-muted-foreground text-sm sm:text-base mb-4 sm:mb-6">{useCase.description}</p>
                      <Link to="/contato">
                        <Button className="gradient-primary text-primary-foreground glow-primary touch-target">
                          Falar sobre meu caso
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </div>

                  {/* Pain Points vs Solutions */}
                  <div className="bg-background p-5 sm:p-8 md:p-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <h4 className="font-semibold text-destructive mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                          <span className="w-3 h-3 rounded-full bg-destructive/20 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                          </span>
                          Dores comuns
                        </h4>
                        <ul className="space-y-2 sm:space-y-3">
                          {useCase.painPoints.map((pain, painIndex) => (
                            <li key={painIndex} className="text-xs sm:text-sm text-muted-foreground flex items-start gap-2 bg-destructive/5 rounded-lg p-2.5 sm:p-3">
                              <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-1.5 sm:mt-2 flex-shrink-0" />
                              {pain}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-primary mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                          <CheckCircle className="h-4 w-4" />
                          Como a Cogni resolve
                        </h4>
                        <ul className="space-y-2 sm:space-y-3">
                          {useCase.solutions.map((solution, solutionIndex) => (
                            <li key={solutionIndex} className="text-xs sm:text-sm text-muted-foreground flex items-start gap-2 bg-primary/5 rounded-lg p-2.5 sm:p-3">
                              <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary mt-0.5 flex-shrink-0" />
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        variant="dark"
        title="Seu caso é diferente?"
        subtitle="Fale com nosso time e veja como podemos adaptar a Cogni para sua operação específica."
        primaryCTA="Conversar sobre meu caso"
      />
    </Layout>
  );
}
